// Lib
import React, { CSSProperties } from 'react';

// Components
import ImageModalDrawingModeContent from '../../content/ImageModalDrawingModeContent';
import ImageModalV2CropModeContent from './ImageModalV2CropModeContent';

// Hooks
import { ImageModalV2Mode } from '../hooks/useImageModalV2Mode';

// Types
import { ImageModalV2EditModeProps } from '../imageModalV2Types';
import { RectSize } from '../../../../../../common/maths/geometry/rect/rectTypes';

type ImageModalV2EditModeContentProps = ImageModalV2EditModeProps & {
    mode: ImageModalV2Mode;
    contentStyle: CSSProperties;
    editOverlayStyle: CSSProperties;
    imageWrapperStyle: CSSProperties;
    imageBounds: RectSize;
};

/**
 * Renders the active editor over the stable V2 image bounds.
 */
const ImageModalV2EditModeContent: React.FC<ImageModalV2EditModeContentProps> = ({
    mode,
    contentStyle,
    editOverlayStyle,
    imageWrapperStyle,
    imageBounds,
    ...editModeProps
}) => {
    // Drawing happens on the cropped (displayed) image, so it uses the cropped footprint.
    const croppedImageWidth = Number(imageWrapperStyle.width);
    // The crop/background editor renders the full (uncropped, possibly rotated) source image inside
    // the stable view-mode frame so switching tools does not resize the outer modal footprint.
    const captionHeight = Math.max(Number(contentStyle.height) - Number(imageWrapperStyle.height), 0);

    switch (mode) {
        case ImageModalV2Mode.DRAW:
            return (
                <div
                    className="ImageModalV2-edit-overlay absolute z-[1] flex items-center justify-center"
                    style={editOverlayStyle}
                >
                    <div className="ImageModalV2-drawing-overlay relative" style={contentStyle}>
                        <div
                            className="ImageModalV2-drawing-canvas absolute top-0 left-0"
                            style={{
                                width: imageWrapperStyle.width,
                                height: imageWrapperStyle.height,
                            }}
                        >
                            <ImageModalDrawingModeContent {...editModeProps} modalWidth={croppedImageWidth} />
                        </div>
                    </div>
                </div>
            );
        case ImageModalV2Mode.CROP:
        case ImageModalV2Mode.BACKGROUND:
            return (
                <div
                    className="ImageModalV2-edit-overlay ImageModalV2-crop-overlay absolute z-[1] flex items-center justify-center"
                    style={editOverlayStyle}
                >
                    <div
                        className="ImageModalV2-editor-overlay-frame relative flex items-center justify-center"
                        style={contentStyle}
                    >
                        <ImageModalV2CropModeContent
                            {...editModeProps}
                            toolbarMode={mode === ImageModalV2Mode.BACKGROUND ? 'background' : 'edit'}
                            boundsWidth={Number(imageBounds.width)}
                            boundsHeight={Number(imageBounds.height)}
                            captionHeight={captionHeight}
                        />
                    </div>
                </div>
            );
        case ImageModalV2Mode.VIEW:
        case ImageModalV2Mode.UPLOAD:
        case ImageModalV2Mode.DOWNLOAD:
            return null;
    }
};

export default ImageModalV2EditModeContent;
